import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Lock, Check, ChevronLeft, RotateCcw, Zap} from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, btn } from "@/components/Page";
import Modal from "@/components/Modal";

const BRANCH_ACCENT = ["text-red-400 border-red-700/40", "text-sky-400 border-sky-700/40", "text-amber-400 border-amber-700/40"];

export default function SkillTree() {
  const { gladId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [respecOpen, setRespecOpen] = useState(false);

  const load = () => api.get(`/gladiators/${gladId}/skill-tree`).then((r) => setData(r.data)).catch((e) => { toast.error(apiErr(e)); navigate("/roster"); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [gladId]);

  const unlock = async (nodeId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${gladId}/skill-tree/unlock`, { node_id: nodeId });
      setData(data);
      toast.success("Technique unlocked");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const respec = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${gladId}/skill-tree/respec`);
      setData(data);
      setRespecOpen(false);
      toast.success(data.gold_spent === 0
        ? `Free respec complete — ${data.refunded} skill points refunded`
        : `Respec complete — ${data.refunded} skill points refunded (${data.gold_spent} gold spent)`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!data) return <Loader label="Reading the training scrolls…" />;
  const respecCost = data.respec_cost ?? 50;
  const freeRespecAvailable = Boolean(data.free_respec_available);
  const canAffordRespec = freeRespecAvailable || (data.gold ?? 0) >= respecCost;

  return (
    <div>
      <button data-testid="skilltree-back-btn" onClick={() => navigate("/roster")} className="flex items-center gap-1 text-zinc-500 hover:text-amber-300 text-sm mb-3 transition-colors">
        <ChevronLeft size={16} /> Back to Roster
      </button>
      <PageHeader
        title={`${data.name} — Skill Tree`}
        subtitle={`${data.cls} · ${data.title} discipline`}
        icon={Sparkles}
        action={
          <div className="flex items-center gap-3">
            <div data-testid="skill-points-counter" className="flex items-center gap-2 px-4 py-2 border border-amber-600/50 bg-amber-950/30">
              <Zap size={16} className="text-amber-400" />
              <span className="font-cinzel text-amber-300 text-lg tabular-nums">{data.skill_points}</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Skill Points</span>
            </div>
            {data.unlocked_count > 0 && (
              <button data-testid="respec-btn" disabled={busy || !canAffordRespec}
                onClick={() => setRespecOpen(true)} title={freeRespecAvailable ? "One free respec available" : `Costs ${respecCost} gold — you have ${data.gold ?? 0}`}
                className={btn("ghost") + " py-2 px-4 flex items-center gap-1.5"}>
                <RotateCcw size={14} /> Respec
                <span data-testid="respec-cost-label" className={`text-[10px] ${freeRespecAvailable ? "text-emerald-400" : !canAffordRespec ? "text-red-400" : "text-zinc-500"}`}>· {freeRespecAvailable ? "Free" : `${respecCost} gold`}</span>
              </button>
            )}
          </div>
        }
      />

      <p className="text-zinc-500 text-sm mb-6 max-w-3xl">
        Spend skill points (earned for every 3 ranked PvP fights) to unlock passive techniques. Each branch unlocks top to bottom. Skill points are capped at 19.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {data.branches.map((br, bi) => (
          <div key={br.id} data-testid={`branch-${br.id}`} className={`border ${BRANCH_ACCENT[bi % 3].split(" ")[1]} bg-zinc-950/60 p-4`}>
            <h3 className={`font-cinzel uppercase tracking-widest text-sm mb-4 ${BRANCH_ACCENT[bi % 3].split(" ")[0]}`}>{br.name}</h3>
            <div className="space-y-3">
              {br.nodes.map((node, ni) => (
                <div key={node.id}>
                  {ni > 0 && <div className="mx-auto w-px h-3 bg-zinc-700" />}
                  <NodeCard node={node} busy={busy} onUnlock={() => unlock(node.id)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Modal open={respecOpen} onClose={() => setRespecOpen(false)} title="Respec Techniques">
        <div data-testid="respec-modal" className="space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Reset <span className="text-amber-300 font-medium">all unlocked techniques</span> for{" "}
            <span className="text-amber-300 font-medium">{data.name}</span> and refund every skill point spent?
          </p>
          <div data-testid="respec-cost-summary" className="flex items-center gap-2 border border-amber-900/40 bg-amber-950/20 px-4 py-3 rounded-sm">
            <GoldCoin size={16} className="text-amber-400" />
            <span className="text-sm text-zinc-300">{freeRespecAvailable ? <span className="font-cinzel text-emerald-300">Your one free respec is available</span> : <>This costs <span className="font-cinzel text-amber-300">{respecCost} gold</span></>}</span>
            {!freeRespecAvailable && <span className="ml-auto text-[11px] uppercase tracking-widest text-zinc-500">You have {data.gold ?? 0}</span>}
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button data-testid="respec-cancel-btn" onClick={() => setRespecOpen(false)} disabled={busy}
              className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm transition-colors">Cancel</button>
            <button data-testid="respec-confirm-btn" onClick={respec} disabled={busy}
              className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide flex items-center gap-1.5 bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm transition-colors disabled:opacity-50">
              <RotateCcw size={14} /> {busy ? "Resetting…" : "Confirm Respec"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function NodeCard({ node, busy, onUnlock }) {
  const state = node.unlocked ? "unlocked" : node.can_unlock ? "available" : "locked";
  const styles = {
    unlocked: "border-emerald-600/50 bg-emerald-950/20",
    available: "border-amber-600/50 bg-amber-950/15",
    locked: "border-zinc-800 bg-zinc-900/40 opacity-70"};
  return (
    <div data-testid={`node-${node.id}`} className={`border ${styles[state]} p-3 transition-all`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-widest text-zinc-600">T{node.tier}</span>
          <span className="font-cinzel text-sm text-zinc-100">{node.name}</span>
        </div>
        {node.unlocked
          ? <Check size={15} className="text-emerald-400 shrink-0" />
          : state === "locked" ? <Lock size={13} className="text-zinc-600 shrink-0" /> : null}
      </div>
      <p className="text-xs text-zinc-400 mt-1.5 leading-snug">{node.desc}</p>
      <div className="flex items-center justify-between mt-2.5">
        <span className="text-[10px] uppercase tracking-widest text-amber-500/80">{node.cost} pts</span>
        {node.unlocked ? (
          <span className="text-[10px] uppercase tracking-widest text-emerald-400">Unlocked</span>
        ) : node.can_unlock ? (
          <button data-testid={`unlock-${node.id}`} disabled={busy} onClick={onUnlock} className="text-[11px] uppercase tracking-widest text-amber-300 border border-amber-700/50 px-2.5 py-1 hover:bg-amber-900/40 transition-all disabled:opacity-40">
            Unlock
          </button>
        ) : (
          <span className="max-w-[12rem] text-right text-[10px] leading-relaxed text-zinc-300">{node.locked_reason}</span>
        )}
      </div>
    </div>
  );
}
